import type { LocalDate } from './dates';
import type { ModuleId } from './modules';

/** Fields every event may carry. */
export interface EventMeta {
  /**
   * Id of the mutation whose write produced this event (e.g. the ledger insert behind
   * 'expense.created'). runRules copies it into `causedBy` on every mutation a rule returns.
   * Absent for system events such as 'day.ended'.
   */
  mutationId?: string;
}

/**
 * Every smoke.* event carries the day's state after the change (v0.3). Rules read a snapshot from
 * before the dispatch (conventions §5), so they can't count the new log themselves.
 */
export interface SmokeDayPayload {
  smokeLogId: string;
  at: string;
  /** Local day the change belongs to (the log's date). */
  date: LocalDate;
  /** Spec §7 day count after the change (core quit/daily.ts tallyDay). */
  dayCount: number;
  /** The day's cigarette cost after the change, in centavos (0 when the plan has no price). */
  dayCostMinor: number;
}

/** Each module plan adds its own members to this union (and a case to eventEntityId). */
export type DomainEvent = EventMeta &
  (
    | {
        type: 'expense.created';
        expenseId: string;
        amountMinor: number;
        categoryId?: string | null;
        /**
         * The fund already written on the expense's insert (the producer resolves it with
         * resolveDeductFund, so no later write has to win over the insert). Null when it has none.
         */
        fundId?: string | null;
        date: LocalDate;
        method?: string | null;
        sourceModule: ModuleId;
        at: string;
      }
    | {
        /**
         * An existing expense changed (v0.2: moved to another fund from the edit sheet). One event
         * per edit with every changed field merged, so a cascade never emits two for the same
         * entity (the cycle guard is per rule and entity, conventions §19 engine review).
         */
        type: 'expense.updated';
        expenseId: string;
        /** Old values of the changed fields (Drizzle property names). */
        before: Record<string, unknown>;
        /** New values of the changed fields. */
        after: Record<string, unknown>;
        sourceModule: string;
        at: string;
      }
    | ({ type: 'smoke.logged' } & SmokeDayPayload)
    | ({ type: 'smoke.total_set' } & SmokeDayPayload)
    | ({ type: 'smoke.removed' } & SmokeDayPayload)
    | { type: 'day.ended'; date: LocalDate }
    | {
        type: 'payday.received';
        paydayId: string;
        sourceId: string;
        amountMinor: number;
        date: LocalDate;
        ledgerEntryId: string;
        at: string;
      }
    | { type: 'payday.skipped'; paydayId: string; sourceId: string; date: LocalDate; at: string }
    | {
        /**
         * Shopping list (v0.2): ticked items were bought for one total. The shopping.expense rule
         * turns it into one Spending expense (Groceries) when Spending is usable.
         */
        type: 'shopping.bought';
        tripId: string;
        totalMinor: number;
        date: LocalDate;
        store: string | null;
        /** The expense note: the items bought. */
        note: string | null;
        /** v0.5: the account it was paid from, resolved by the producer (absent: the rule's default). */
        accountId?: string | null;
        /** v0.7.6: 'card:<payee id>' when it was charged to a card (then no account). */
        method?: string | null;
        /** The expense's id, already on the trip insert (null: the rule makes one and links it). */
        expenseId?: string | null;
        at: string;
      }
    | {
        /**
         * Payments (v0.4): a payment toward a due, or any payment on a flexible loan. The
         * payments.paid-expense rule writes its ledger row (expense, or a transfer for a card
         * statement) on ledgerEntryId, which the producer set only while Spending is usable.
         */
        type: 'payment.paid';
        paymentId: string;
        payeeId: string;
        payeeKind: 'bill' | 'utility' | 'card' | 'loan';
        ledgerEntryId: string | null;
        /** A card statement payment: a transfer, never spending. */
        transfer: boolean;
        amountMinor: number;
        /** The expense's date (a late payment counts on its due date). */
        date: LocalDate;
        categoryId: string | null;
        /** v0.5: the account it was paid from (picked, the payee's, or Budget's "Pay from"). */
        accountId?: string | null;
        /** The payee's fund (null = Budget's default rule). */
        fundId: string | null;
        note: string | null;
        at: string;
      }
    | {
        /**
         * Weight (v0.7): a day's weigh-in was logged or replaced. No rule listens yet (Journal's
         * recap and Habits' gym insight will).
         */
        type: 'weight.logged';
        entryId: string;
        date: LocalDate;
        valueG: number;
        at: string;
      }
  );

export type DomainEventType = DomainEvent['type'];

/** The event member for one type: EventOf<'day.ended'> */
export type EventOf<T extends DomainEventType> = Extract<DomainEvent, { type: T }>;

/**
 * The entity an event is about. The rule engine's cycle guard skips a rule that already ran
 * for the same entity. Adding a union member without a case here is a type error.
 */
export function eventEntityId(event: DomainEvent): string {
  switch (event.type) {
    case 'expense.created':
    case 'expense.updated':
      return event.expenseId;
    case 'smoke.logged':
    case 'smoke.total_set':
    case 'smoke.removed':
      return event.smokeLogId;
    case 'day.ended':
      return event.date;
    case 'payday.received':
    case 'payday.skipped':
      return event.paydayId;
    case 'shopping.bought':
      return event.tripId;
    case 'payment.paid':
      return event.paymentId;
    case 'weight.logged':
      return event.entryId;
  }
}
